import { IAttachment, IUserData } from '@/models';
import {
  IPaymentRequisitionService,
  IPaymentRequisitionPaginatedListData,
  IPaymentRequisitionExportFilterParams,
  PAYMENT_REQUISITION_EXPORT_TIMEOUT_IN_MILLISECONDS,
  IPaymentRequisitionFilterParam
} from './paymentrequisition.types';
import { AxiosInstance } from 'axios';
import { objectToFormData } from '@/utils/objectToFormData';
import { Transition } from '@/models/transition';
import {
  IPaymentRequisitionCommentData,
  IPaymentRequisitionData,
  IPaymentRequisitionLineData,
  PaymentRequisitionStatusEnum
} from '@/models/payment-requisition';

export class PaymentRequisitionService implements IPaymentRequisitionService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/payment-requisitions/';
  }

  // TODO: LH - 2022-12-22 - let's encapsulate these paramaters
  fetchPaymentRequisitions(
    filter?: IPaymentRequisitionFilterParam
  ): Promise<IPaymentRequisitionPaginatedListData> {
    return this.axios
      .get<IPaymentRequisitionPaginatedListData>(`${this.endpoint}`, {
        timeout: 1 * (60 * 1000), //1 minute
        params: filter
      })
      .then(({ data }) => data);
  }

  fetchPaymentRequisition(id: number): Promise<IPaymentRequisitionData> {
    return this.axios
      .get<IPaymentRequisitionData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  createPaymentRequisition(
    data: IPaymentRequisitionData
  ): Promise<IPaymentRequisitionData> {
    const formData = objectToFormData(data);

    return this.axios
      .post<IPaymentRequisitionData>(`${this.endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  updatePaymentRequisition(
    data: IPaymentRequisitionData
  ): Promise<IPaymentRequisitionData> {
    const formData = objectToFormData(data);

    return this.axios
      .patch<IPaymentRequisitionData>(`${this.endpoint}${data.id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  deletePaymentRequisition(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`);
  }

  updatePaymentRequisitionLine(
    paymentRequisitionId: number,
    data: IPaymentRequisitionLineData
  ): Promise<IPaymentRequisitionLineData> {
    return this.axios
      .patch<IPaymentRequisitionLineData>(
        `${this.endpoint}${paymentRequisitionId}/lines/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deletePaymentRequisitionAttachment(
    PaymentRequisitionId: number,
    attachmentId: number
  ): Promise<any> {
    return this.axios
      .delete(
        `${this.endpoint}${PaymentRequisitionId}/remove-attachment/${attachmentId}/`
      )
      .then(({ data }) => data);
  }

  updatePaymentRequisitionState(
    id: number,
    state: PaymentRequisitionStatusEnum,
    assignee?: number,
    comment?: string
  ): Promise<IPaymentRequisitionData> {
    return this.axios
      .put(`${this.endpoint}${id}/set_state/${state}/`, {
        assignee,
        comment
      })
      .then(({ data }) => data);
  }

  fetchRequisitionAPSpecialists(id: number): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(`${this.endpoint}${id}/ap_specialists/`)
      .then(({ data }) => data);
  }

  assignRequisitionAPSpecialist(
    id: number,
    apSpecialist: number
  ): Promise<IPaymentRequisitionData> {
    return this.axios
      .put(`${this.endpoint}${id}/assign_ap_specialist/`, {
        ap_specialist: apSpecialist
      })
      .then(({ data }) => data);
  }

  fetchPaymentRequisitionApprovers(id: number): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(`${this.endpoint}${id}/approvers/`)
      .then(({ data }) => data);
  }

  assignPaymentRequisitionApprover(
    id: number,
    approver: number
  ): Promise<IPaymentRequisitionData> {
    return this.axios
      .put(`${this.endpoint}${id}/assign_approver/`, {
        approver
      })
      .then(({ data }) => data);
  }

  updatePaymentRequisitionOwner(
    id: number,
    owner: number
  ): Promise<IPaymentRequisitionData> {
    return this.axios
      .put(`${this.endpoint}${id}/set-owner/`, {
        owner
      })
      .then(({ data }) => data);
  }

  fetchPaymentRequisitionActivities(id: number): Promise<Transition[]> {
    return this.axios
      .get<Transition[]>(`${this.endpoint}${id}/activities/`)
      .then(({ data }) => data);
  }

  // comments
  fetchPaymentRequisitionComments(
    requisitionId: number
  ): Promise<IPaymentRequisitionData[]> {
    return this.axios
      .get<IPaymentRequisitionData[]>(
        `${this.endpoint}${requisitionId}/comments/`
      )
      .then(({ data }) => data);
  }

  fetchPaymentRequisitionComment(
    requisitionId: number,
    commentId: number
  ): Promise<IPaymentRequisitionData> {
    return this.axios
      .get<IPaymentRequisitionData>(
        `${this.endpoint}${requisitionId}/comments/${commentId}/`
      )
      .then(({ data }) => data);
  }

  createPaymentRequisitionComment(
    requisitionId: number,
    comment: IPaymentRequisitionCommentData
  ): Promise<IPaymentRequisitionData> {
    return this.axios
      .post<IPaymentRequisitionData>(
        `${this.endpoint}${requisitionId}/comments/`,
        comment
      )
      .then(({ data }) => data);
  }

  updatePaymentRequisitionComment(
    requisitionId: number,
    comment: IPaymentRequisitionCommentData
  ): Promise<IPaymentRequisitionData> {
    return this.axios
      .patch<IPaymentRequisitionData>(
        `${this.endpoint}${requisitionId}/comments/${comment.id}/`,
        comment
      )
      .then(({ data }) => data);
  }

  deletePaymentRequisitionComment(
    requisitionId: number,
    commentId: number
  ): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${requisitionId}/comments/${commentId}/`)
      .then(({ data }) => data);
  }

  addPaymentRequisitionCommentAttachments(
    requisitionId: number,
    commentId: number,
    attachments: IAttachment[]
  ): Promise<IPaymentRequisitionCommentData> {
    const formData = objectToFormData({ attachments });
    return this.axios
      .post<IPaymentRequisitionCommentData>(
        `${this.endpoint}${requisitionId}/comments/${commentId}/add-attachments/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  removePaymentRequisitionCommentAttachment(
    requisitionId: number,
    commentId: number,
    attachmentId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${requisitionId}/comments/${commentId}/remove-attachment/${attachmentId}/`
      )
      .then();
  }

  fetchPaymentRequisitionCommentsMentionableUsers(
    requisitionId: number
  ): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(
        `${this.endpoint}${requisitionId}/comments/mentionable-users/`
      )
      .then(({ data }) => data);
  }

  exportPaymentRequisitionPdf(requisitionId: number): Promise<Blob> {
    return this.axios
      .get(`${this.endpoint}${requisitionId}/export_pdf/`, {
        responseType: 'blob'
      })
      .then(({ data }) => data);
  }

  exportPaymentRequisitions(
    params?: IPaymentRequisitionExportFilterParams
  ): Promise<Blob> {
    return this.axios
      .get(`${this.endpoint}export-csv/`, {
        responseType: 'blob',
        timeout: PAYMENT_REQUISITION_EXPORT_TIMEOUT_IN_MILLISECONDS,
        params: params
      })
      .then(({ data }) => data);
  }

  updateMultiplePaymentRequisitionOwner(
    owner: number,
    requisitions: number[]
  ): Promise<void> {
    return this.axios
      .post<void>(`${this.endpoint}bulk-set-owner/`, {
        requisitions,
        owner
      })
      .then(({ data }) => data);
  }
}
